import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { groupsApi } from '../../services/groups-api-client';
import type { RoomConfirmationResult } from '../../types/groups';

/**
 * Bulk "send confirmations": previews which booked guests haven't been confirmed
 * yet (a dry run), then sends them their stay-confirmation emails on confirm.
 */
export default function SendConfirmationsDialog({
  groupId,
  requestIds,
  onClose,
  onSent,
}: {
  groupId: string;
  /** When set, send to exactly these reservations (re-sends allowed); otherwise everyone not yet confirmed. */
  requestIds?: string[];
  onClose: () => void;
  onSent: () => void;
}) {
  const [pending, setPending] = useState<RoomConfirmationResult[] | null>(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    groupsApi
      .sendRoomConfirmations(groupId, { dryRun: true, requestIds })
      .then((res) => { if (active) setPending(res.results); })
      .catch((e) => { if (active) setError(e?.response?.data?.error || 'Could not load pending confirmations.'); });
    return () => { active = false; };
  }, [groupId, requestIds]);

  // The preview returns everything it looked at, including reservations it will
  // not send — no contact on file, or nothing to state. Counting those as
  // recipients is how a host plans around confirmations that never go out.
  const sendable = (pending ?? []).filter((p) => p.status === 'preview');
  const heldBack = (pending ?? []).filter((p) => p.status !== 'preview');

  const handleSend = async () => {
    setSending(true);
    setError('');
    try {
      // Send exactly what the dry run said it would, not the raw selection — so
      // anything the preview filtered out can't slip into the real send. What
      // you saw is what goes out.
      await groupsApi.sendRoomConfirmations(groupId, {
        dryRun: false,
        requestIds: sendable.map((p) => p.requestId),
      });
      onSent();
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Sending failed. Nothing was double-sent — try again.');
      setSending(false);
    }
  };

  const selection = (requestIds?.length ?? 0) > 0;

  const count = sendable.length;

  return (
    <Dialog open onClose={sending ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>Send confirmations</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {pending === null && !error ? (
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 2 }}>
            <CircularProgress size={20} /><Typography>Checking who still needs a confirmation…</Typography>
          </Stack>
        ) : count === 0 && heldBack.length === 0 ? (
          <Alert severity="info">
            {selection
              ? 'None of the selected reservations can be confirmed (they must be booked).'
              : 'Everyone with a booked reservation has already been sent their confirmation.'}
          </Alert>
        ) : (
          <Stack spacing={1.5}>
            {count === 0 ? (
              <Alert severity="warning">
                Nothing can be sent yet — every reservation below is missing something.
              </Alert>
            ) : (
              <Typography>
                {selection
                  ? `${count} selected guest${count === 1 ? '' : 's'} will get their stay-confirmation email${count === 1 ? '' : ' — some may be re-sends'}.`
                  : `${count} guest${count === 1 ? '' : 's'} with a booked room ${count === 1 ? 'hasn’t' : 'haven’t'} been confirmed yet. They'll each get their stay-confirmation email.`}
              </Typography>
            )}
            {count > 0 ? (
              <List dense sx={{ maxHeight: 200, overflow: 'auto', bgcolor: 'action.hover', borderRadius: 2 }}>
                {sendable.map((r) => (
                  <ListItem key={r.requestId}>
                    <ListItemText primary={r.guestName} secondary={r.email || 'no email'} />
                  </ListItem>
                ))}
              </List>
            ) : null}

            {heldBack.length > 0 ? (
              <>
                <Typography variant="body2" color="text.secondary">
                  {heldBack.length} won't be sent:
                </Typography>
                <List dense sx={{ maxHeight: 160, overflow: 'auto', bgcolor: 'action.hover', borderRadius: 2 }}>
                  {heldBack.map((r) => (
                    <ListItem key={r.requestId}>
                      <ListItemText
                        primary={r.guestName}
                        secondary={r.reason || 'Not ready to send.'}
                      />
                    </ListItem>
                  ))}
                </List>
              </>
            ) : null}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={sending}>Cancel</Button>
        <Button variant="contained" onClick={handleSend} disabled={sending || count === 0}>
          {sending ? 'Sending…' : count > 0 ? `Send ${count} confirmation${count === 1 ? '' : 's'}` : 'Send'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
